import '../styles/score-cards.css';

import dayjs from 'dayjs';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEnv } from '@/domain/impact-calculate-venue/entities/calculate-venue-env.entity';
import { CalculateVenueEss } from '@/domain/impact-calculate-venue/entities/calculate-venue-ess.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueEnvForProject,
  selectImpactCalculateVenueEssForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { formatProjectDatesTz } from '../../../utils/format-project-dates';
import { extendVenueEnvCalculate } from '../../pdf/utils/compute-extend-env';
import { extendVenueEssCalculate } from '../../pdf/utils/compute-extend-ess';
import ArrowIcon from '../assets/arrow.svg';
import FairlyScorePillLogo from '../assets/fairly-score-pill-logo.svg';
import { MainScorePill } from '../components/MainScorePill';

export function ScoreCardsVenueC1() {
  document.documentElement.classList.add('score-card');
  document.body.classList.add('score-card');
  dayjs.locale('fr');

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  const impactEnv = extendVenueEnvCalculate(
    useAppSelector(selectImpactCalculateVenueEnvForProject(venueId)),
  ) satisfies CalculateVenueEnv;

  const impactEss = extendVenueEssCalculate(
    useAppSelector(selectImpactCalculateVenueEssForProject(venueId)),
  ) satisfies CalculateVenueEss;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  return (
    <div id="card1" className="score-card-summary">
      <div className="title">
        <h1>{venue.name}</h1>
        <h2>
          {formatProjectDatesTz(venue.startDate || '', venue.endDate || '')}
        </h2>
      </div>
      <div className="scores">
        <div className="score-row">
          <div className="score-row-left">
            <img src={ArrowIcon} width="50" height="50" />
            {translate('Projects.co2')}
          </div>
          <MainScorePill
            scoreLetter={impactCo2.global.scoreLetter}
            type="co2"
          />
        </div>
        <div className="score-row">
          <div className="score-row-left">
            <img src={ArrowIcon} width="50" height="50" />
            {translate('Projects.env')}
          </div>
          <MainScorePill
            scoreLetter={impactEnv.global.scoreLetter}
            type="env"
          />
        </div>
        <div className="score-row">
          <div className="score-row-left">
            <img src={ArrowIcon} width="50" height="50" />
            {translate('Projects.ess')}
          </div>
          <MainScorePill
            scoreLetter={impactEss.global.scoreLetter}
            type="ess"
          />
        </div>
      </div>

      <div className="copyright">
        <img
          src={FairlyScorePillLogo}
          width="160"
          height="160"
          alt="Fairly logo"
          style={{ margin: '-1em' }}
        />
      </div>

      <div className="edition-date">
        {translate('PdfExports.edition-date', { date: dayjs().format('LL') })}
      </div>
    </div>
  );
}
